import { useState } from "react";
import useSWR from "swr";
import { Download, Trash2, Save, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const fetcher = (url) => adminApi.get(url);

const STATUS_LABELS = {
  new: "New", contacted: "Contacted", qualified: "Qualified", quotation_sent: "Quotation Sent",
  sample_requested: "Sample Requested", negotiation: "Negotiation", converted: "Converted", closed: "Closed",
};

export default function LeadsPanel() {
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState("");

  const { data, mutate, isLoading } = useSWR(
    `/admin/leads?type=${type}&status=${status}&q=${encodeURIComponent(query)}&page=${page}`,
    fetcher,
    { keepPreviousData: true }
  );

  const setStatusFor = async (id, s) => {
    await adminApi.put(`/admin/leads/${id}`, { status: s });
    toast.success("Status updated");
    mutate();
  };
  const addNote = async (id) => {
    if (!note.trim()) return;
    await adminApi.put(`/admin/leads/${id}`, { add_note: note.trim() });
    setNote("");
    toast.success("Note added");
    mutate();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminApi.del(`/admin/leads/${id}`);
    toast.success("Lead deleted");
    mutate();
  };

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setStatus(""); setPage(1); }} className={`chip ${!status ? "!border-gold !bg-gold/10 !text-gold-dark" : ""}`}>
            All ({data?.counts?.all ?? 0})
          </button>
          {Object.entries(STATUS_LABELS).map(([k, label]) => (
            <button key={k} onClick={() => { setStatus(k); setPage(1); }} data-testid={`lead-status-filter-${k}`} className={`chip ${status === k ? "!border-gold !bg-gold/10 !text-gold-dark" : ""}`}>
              {label} ({data?.counts?.[k] ?? 0})
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <select className="field !w-auto !py-2" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} data-testid="lead-type-filter" aria-label="Filter by type">
            <option value="">All types</option>
            <option value="quote">Quotes</option>
            <option value="contact">Contacts</option>
            <option value="sample">Samples</option>
          </select>
          <form className="flex" onSubmit={(e) => { e.preventDefault(); setQuery(q); setPage(1); }}>
            <input className="field !w-44 !py-2" placeholder="Search leads…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search-input" aria-label="Search leads" />
          </form>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export`} className="btn-navy !px-4 !py-2 !text-[11px]" data-testid="leads-export-csv">
            <Download size={14} /> CSV
          </a>
        </div>
      </div>

      {isLoading && !data ? <div className="card mt-5 h-40 animate-pulse" /> : (
        <div className="mt-5 space-y-3">
          {(data?.items || []).length === 0 && (
            <div className="card p-8 text-center text-sm text-mist" data-testid="leads-empty-state">No leads match the current filters.</div>
          )}
          {(data?.items || []).map((lead) => (
            <div key={lead.id} className="card p-4" data-testid={`lead-row-${lead.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="chip !text-[10px] uppercase">{lead.type}</span>
                    <h3 className="font-bold text-navy">{lead.name}</h3>
                    {lead.company && <span className="text-xs text-mist">· {lead.company}</span>}
                  </div>
                  <p className="mt-1 text-xs text-mist">
                    {lead.product || "—"} {lead.quantity && `· ${lead.quantity}`} {lead.country && `· ${lead.country}`} · {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="field !w-auto !py-1.5 !text-xs"
                    value={lead.status}
                    onChange={(e) => setStatusFor(lead.id, e.target.value)}
                    data-testid={`lead-status-select-${lead.id}`}
                    aria-label="Change lead status"
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => setExpanded(expanded === lead.id ? null : lead.id)} data-testid={`lead-expand-${lead.id}`} className="rounded-lg border border-line p-2 text-navy hover:border-gold" aria-label="Toggle details">
                    <ChevronDown size={14} className={expanded === lead.id ? "rotate-180 transition-transform" : "transition-transform"} />
                  </button>
                  <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`} className="rounded-lg border border-line p-2 text-red-600 hover:border-red-300" aria-label="Delete lead">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {expanded === lead.id && (
                <div className="mt-4 border-t border-line pt-4 text-sm" data-testid={`lead-details-${lead.id}`}>
                  <div className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2">
                    {[["Email", lead.email], ["Phone / WhatsApp", lead.phone], ["Packaging", lead.packaging],
                      ["Destination Country", lead.destination_country], ["Destination Port", lead.destination_port],
                      ["Expected Delivery", lead.delivery_date], ["Private Label", lead.private_label],
                      ["Attachment", lead.attachment_url && <a key="att" href={lead.attachment_url} target="_blank" rel="noopener noreferrer" className="text-gold-dark underline">{lead.attachment_name || "View file"}</a>]
                    ].map(([k, v]) => (
                      <p key={k}><span className="font-bold text-navy">{k}: </span>{v || "—"}</p>
                    ))}
                  </div>
                  {lead.message && <p className="mt-3 rounded-lg bg-sand p-3 text-ink">{lead.message}</p>}
                  <div className="mt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-mist">Notes</h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-mist">
                      {(lead.notes || []).map((n, i) => <li key={i} className="rounded border border-line bg-white px-3 py-2">{n.text} · {new Date(n.at).toLocaleString()}</li>)}
                      {(lead.notes || []).length === 0 && <li>No notes yet.</li>}
                    </ul>
                    <div className="mt-2 flex gap-2">
                      <input className="field !py-2" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`lead-note-input-${lead.id}`} />
                      <button onClick={() => addNote(lead.id)} className="btn-navy !px-4 !py-2 !text-[11px]" data-testid={`lead-note-save-${lead.id}`}><Save size={13} /> Add</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {(data?.total || 0) > data?.per_page && (
            <div className="flex items-center justify-between pt-2 text-xs text-mist">
              <span>Page {data.page} of {Math.ceil(data.total / data.per_page)}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border border-line px-3 py-1.5 font-bold disabled:opacity-40">Prev</button>
                <button disabled={page >= Math.ceil(data.total / data.per_page)} onClick={() => setPage((p) => p + 1)} className="rounded border border-line px-3 py-1.5 font-bold disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
